import Stardog, { Connection as StardogConnection } from 'stardog';

import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  GenerateConnectionTokenDocument,
  GenerateConnectionTokenQuery,
  GetConnectionByIndexDocument,
  GetConnectionByIndexQuery,
} from 'src/ui/graph/types';

/**
 * Mint a genuinely new token for a connection after Stardog rejected the
 * current one.
 *
 * The server caches the Portal-issued JWT it hands out, so re-reading
 * `connection.token` here would return the same rejected string. Only
 * `generateConnectionToken` with `forceRefresh` bypasses that cache — and it
 * needs the connection id, so the index has to be resolved first.
 */
const refetchConnectionToken = async (
  connectionIdx: number,
  org: string
): Promise<string | null> => {
  const connectionData = await graphQLClient.request<GetConnectionByIndexQuery>(
    GetConnectionByIndexDocument,
    { index: connectionIdx, org_domain: org }
  );
  const connectionId = connectionData?.connection?.id;
  if (!connectionId) {
    return null;
  }

  const tokenData = await graphQLClient.request<GenerateConnectionTokenQuery>(
    GenerateConnectionTokenDocument,
    { connectionId, forceRefresh: true }
  );
  return tokenData?.generateConnectionToken?.access_token || null;
};

export const runWithRefetchConnectionToken = (org = '') => async <
  HttpBody extends { status: number } = Stardog.HTTP.Body
>(
  stardogConnection: StardogConnection,
  connectionIdx: number | undefined,
  wrappedRequest: (stardogConn: StardogConnection) => Promise<HttpBody>
) => {
  let response = await wrappedRequest(stardogConnection);
  if (connectionIdx !== undefined && response && response.status === 401) {
    const updatedToken = await refetchConnectionToken(connectionIdx, org);
    if (updatedToken) {
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      stardogConnection.token = updatedToken;
      response = await wrappedRequest(stardogConnection);
    }
  }
  return response;
};
